/**
 * Referral queue (1.2): what has come in and has not been decided.
 *
 * There is no status column, because everything here is undecided: a referral
 * leaves this queue on Accept (to Pending admissions) or on Release (to
 * neither). The small dot beside the patient is how the checks came out, not a
 * recommendation; the panel says why.
 *
 * SOC by is a **day**, never a time. The 48-hour clock runs from the expected
 * discharge and the column shows the day it lands on.
 */
import { useMemo, useState } from "react";
import { Box, Button, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { KeyedReferralDialog } from "./KeyedReferralDialog";
import { schedulerFoundSlot } from "./useSocScheduleOptions";
import { useNavigate } from "react-router-dom";
import { useNow } from "../../components/useNow";
import { dateKeyStart, formatInBranchZone } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { expectedPlanFor } from "../../engine/expectedPlan";
import { socDueByIso } from "../../engine/holds";
import { selectReferralChecks, type CheckSummary } from "../../engine/referralChecks";
import { useDemoState } from "../../store/useDemoStore";
import { useBranchClock } from "../../components/branchClock";

const SUMMARY_DOT: Record<CheckSummary, string> = { clear: "#2e7d32", marginal: "#b26a00", blocked: "#c62828" };

export function ReferralQueue({ selectedId }: { selectedId?: string }) {
  const [adding, setAdding] = useState(false);
  const state = useDemoState();
  const navigate = useNavigate();
  const now = useNow();
  const tz = state.config.branch.timeZone;
  const clock = useBranchClock(tz);
  // Undecided only: not accepted, not released, and on the queue rather than still with intake.
  const referrals = useMemo(
    () => Object.values(state.referrals).filter((r) => r.status === "new").sort((a, b) => b.receivedAtIso.localeCompare(a.receivedAtIso)),
    [state.referrals],
  );
  /**
   * **The dot answers what the list can actually know.**
   *
   * The panel asks the product's scheduler whether anybody has a slot; the queue
   * cannot, without running the optimizer once per row. So its own search
   * answered instead — and for a referral the scheduler could place, it said
   * there was no slot and drew a red dot above a panel whose checks were all
   * green (Pete, 2026-09-24).
   *
   * A list that contradicts the thing it links to is worse than a list that says
   * less, so the dot is the checks the queue can stand behind — the service area
   * and the branch's capacity — and the SOC question is left to the panel, which
   * is the screen that asks it.
   */
  const summaries = useMemo(
    () =>
      Object.fromEntries(
        referrals.map((r) => {
          // What the list can judge for itself: the service area, and whether
          // taking this patient puts a territory over its target.
          const checks = selectReferralChecks(state, r.id, { wallNow: now })?.checks.filter((c) => c.key !== "socWithin48h") ?? [];
          const local: CheckSummary = checks.some((c) => c.status === "fail") ? "blocked" : checks.some((c) => c.status === "warn" || c.status === "caution") ? "marginal" : "clear";
          // A no-go it can see for itself — a zip outside the branch — is worth
          // saying at once, without waiting for anybody.
          if (local === "blocked") return [r.id, local];
          /**
           * Everything else waits for the scheduler, which only the panel asks.
           * Nothing until somebody opens the referral; afterwards, what it found
           * **and** what the list already knew — a referral the scheduler can
           * place into a territory that is over target is amber on the panel, so
           * it is amber here (Pete, 2026-09-24).
           */
          const found = schedulerFoundSlot(r.id);
          return [r.id, found === undefined ? undefined : found ? local : ("blocked" as CheckSummary)];
        }),
      ),
    [state, referrals, now],
  );

  return (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Patient</TableCell>
            <TableCell>Received</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Expected plan</TableCell>
            <TableCell>Payer</TableCell>
            <TableCell>Expected discharge</TableCell>
            <TableCell>SOC by</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {referrals.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="body2" color="text.secondary">
                  No pending referrals
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {referrals.map((r) => {
            const patient = state.patients[r.patientRef];
            const summary = summaries[r.id];
            const plan = expectedPlanFor(state.config, r);
            const selected = r.id === selectedId;
            return (
              <TableRow key={r.id} hover selected={selected} onClick={() => navigate(`/referrals/${r.id}`)} sx={{ cursor: "pointer" }}>
                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {summary && <Box aria-label={`service area and capacity ${summary}`} sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: SUMMARY_DOT[summary], flexShrink: 0 }} />}
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {patient?.displayName ?? r.patientRef}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {patient?.zip} · {patient?.territory ?? "—"}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>{clock.dateTime(r.receivedAtIso)}</TableCell>
                <TableCell sx={{ maxWidth: 220 }}>{r.source}</TableCell>
                <TableCell>
                  <Typography variant="body2">{plan.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {plan.orders.map((o) => `${o.discipline}${o.requiredTraits?.length ? ` (${o.requiredTraits.join(", ")})` : ""}`).join(" · ")}
                  </Typography>
                </TableCell>
                <TableCell>{patient?.payer === "MA" ? "Medicare Advantage" : "Medicare FFS"}</TableCell>
                <TableCell>{formatInBranchZone(dateKeyStart(r.expectedDischarge, tz), tz, "EEE MMM d")}</TableCell>
                <TableCell>{formatInBranchZone(new Date(socDueByIso(r, tz)), tz, "EEE MMM d")}</TableCell>
              </TableRow>
            );
          })}
          {/* **Keyed in by hand** (Pete, 2026-09-24), for a branch whose referrals
              arrive by phone or fax. Below the list and styled as the product's
              own add-new rows are, because that is what it is. */}
          <TableRow>
            <TableCell colSpan={7}>
              <Button size="small" startIcon={<AddCircleOutlineIcon fontSize="small" />} onClick={() => setAdding(true)}>
                Add New Referral
              </Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <KeyedReferralDialog open={adding} onClose={() => setAdding(false)} onAdded={(ref) => navigate(`/referrals/${ref}`)} />
    </Paper>
  );
}
